import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  FormControl,
  Button,
  Alert,
} from "react-bootstrap";
import { updateUserAttribute } from "aws-amplify/auth"; // Import function to update user attributes
import { Pencil } from "react-bootstrap-icons"; // Import Pencil icon for the edit button
import useForm from "./UseForm"; // Import custom hook for form handling
import { useSelector, useDispatch } from "react-redux";
import { setUserAttributes } from "../../redux/slices/userSlice"; // Import Redux action to set user attributes

export default function User({ signOut, user }) {
  const [isEditing, setIsEditing] = useState(false); // State to track if the form is in edit mode

  // Access userAttributes from Redux state
  const userAttributes = useSelector((state) => state.user.userAttributes);
  const dispatch = useDispatch(); // Initialize the Redux dispatch function

  // Destructure values, handleChange, handleSubmit, setValues, setErrors, and errors from useForm hook
  const { values, handleChange, handleSubmit, setValues, setErrors, errors } =
    useForm(handleSaveClick);

  // useEffect to set initial form values when userAttributes change
  useEffect(() => {
    if (userAttributes && userAttributes.name) {
      console.log("Setting initial form values from user attributes.");
      setValues({ name: userAttributes.name }); // Set form values to the user's name
    }
  }, [userAttributes, setValues]);

  // Function to handle the edit button click event
  function handleEditClick() {
    console.log("Edit button clicked. Entering edit mode.");
    setIsEditing(true); // Enable edit mode
  }

  // Function to handle saving the updated user attribute
  async function handleSaveClick() {
    console.log("Saving new user attribute:", values.name);
    try {
      const attributeKey = "name"; // The attribute key to update
      const value = values.name; // The new value for the attribute
      const output = await updateUserAttribute({
        userAttribute: {
          attributeKey,
          value,
        },
      });
      handleUpdateUserAttributeNextSteps(output); // Handle the next steps after updating the attribute
    } catch (error) {
      console.error("Error saving user attribute:", error.message);
      setErrors({ form: error.message }); // Set form error if the update fails
    }
  }

  // Function to handle the next steps after updating the user attribute
  function handleUpdateUserAttributeNextSteps(output) {
    const { nextStep } = output;
    console.log("Next step after updating user attribute:", nextStep);
    switch (nextStep.updateAttributeStep) {
      case "CONFIRM_ATTRIBUTE_WITH_CODE":
        const codeDeliveryDetails = nextStep.codeDeliveryDetails;
        console.log(
          `Confirmation code was sent to ${codeDeliveryDetails?.deliveryMedium}.`
        );
        // Handle the confirmation code input process here if needed
        break;
      case "DONE":
        console.log("User attribute updated successfully. Exiting edit mode.");
        dispatch(setUserAttributes({ ...userAttributes, name: values.name })); // Update the Redux state with the new name
        setIsEditing(false); // Exit edit mode
        break;
      default:
        console.log("Unknown step in update process.");
    }
  }

  return (
    <Container>
      <Row>
        <Col md={1}></Col>
        <Col>
          <h1>Hello {userAttributes.name}</h1> {/* Display the user's name */}
        </Col>
        <Col md={1}></Col>
      </Row>
      <Row className="mt-2">
        <Col md={1}></Col>
        <Col md={6}>
          <p>Email: {userAttributes.email}</p> {/* Display the user's email */}
          <div className="d-flex align-items-center mt-2">
            {isEditing ? (
              <>
                {/* Editable form control for the user's name */}
                <FormControl
                  id="name"
                  name="name"
                  value={values.name || ""}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className="mr-2"
                />
                <Button variant="primary" onClick={handleSubmit}>
                  Save
                </Button>
                {errors.form && (
                  <Alert variant="danger" className="ml-2">
                    {errors.form}
                  </Alert>
                )}
              </>
            ) : (
              <>
                {/* Display the user's name with an edit button */}
                <p className="mb-0">Name: {userAttributes.name}</p>
                <Button
                  variant="outline-primary"
                  onClick={handleEditClick}
                  className="ml-2"
                >
                  <Pencil width={16} height={16} />
                </Button>
              </>
            )}
          </div>
        </Col>
        <Col md={4} className="d-flex justify-content-end">
          {/* Placeholder image for the user's profile picture */}
          <img
            src="https://via.placeholder.com/150"
            alt="Round"
            className="rounded-circle"
            width="150"
            height="150"
          />
        </Col>
        <Col md={1}></Col>
      </Row>
      <Row>
        <Col md={1}></Col>
        <Col md={6}>
          <p>Statistics:</p>
          <div>
            <p>Registered: {userAttributes.registeredDate}</p>{" "}
            {/* Display the registration date */}
          </div>
          <div>
            <p>Words learned:</p> {/* Placeholder for words learned */}
          </div>
        </Col>
        <Col md={4}></Col>
        <Col md={1}></Col>
      </Row>
    </Container>
  );
}
